import type { UnifiedAuthStep } from '~kui/types/auth';
import type { Ref } from 'vue';

import type { EntryCta, EntrySource, LoginFlowType, SiteRoleType } from '~/graphql/generated/graphql';
import type { SocialReturnParams } from '~/utils/unified/socialReturn';

import { pickSelectedEntityQueryParams } from '~/utils/unified/selectedEntity';
import { parseSocialReturnFromQuery } from '~/utils/unified/socialReturn';

import type { UnifiedAuthState } from './useUnifiedAuthFlow';

interface SocialAuthCallbackOptions {
  /** The login door, reported on the page-view and login-started events. */
  roleType: SiteRoleType;
  authState: Ref<UnifiedAuthState>;
  loading: Ref<boolean>;
  socialAuthPending: Ref<boolean>;
  goToStep: (step: UnifiedAuthStep) => void;
  authStartTime: Ref<number>;
  entrySource: EntrySource | null;
  entryCta: EntryCta | null;
}

export const useSocialAuthCallback = ({
  roleType,
  authState,
  loading,
  socialAuthPending,
  goToStep,
  authStartTime,
  entrySource,
  entryCta,
}: SocialAuthCallbackOptions) => {
  const route = useRoute();
  const router = useRouter();
  const { $analytics, $logWarnMessage } = useNuxtApp();

  // Existing-role social logins are completed in pages/unified/auth/callback.vue
  // before this composable ever mounts. Anything that lands here with social_flow
  // params is a new-role registration that needs the name/details steps.
  const handleSocialLoginResult = async (result: SocialReturnParams) => {
    loading.value = true;

    authState.value.flow = result.flow as LoginFlowType;
    authState.value.email = result.email;
    authState.value.firstName = result.firstName;
    authState.value.lastName = result.lastName;

    $analytics.track(EVENTS.UNIFIED_AUTH_REGISTRATION_STARTED, {
      method: `social_${result.provider.toLowerCase()}`,
      entrySource,
      entryCta,
    });
    socialAuthPending.value = false;
    loading.value = false;
    goToStep('name');
  };

  const cleanupQueryParams = () => {
    const preservedQuery: Record<string, string> = {};
    const referralCode = route.query.referralCode;
    if (typeof referralCode === 'string') {
      preservedQuery.referralCode = referralCode;
    }
    Object.assign(preservedQuery, pickSelectedEntityQueryParams(route.query));
    void router.replace({ path: route.path, query: preservedQuery });
  };

  onMounted(() => {
    const socialReturn = parseSocialReturnFromQuery(route.query);
    if (socialReturn) {
      if (route.query.authStartTime) {
        authStartTime.value = Number(route.query.authStartTime);
      }
      void handleSocialLoginResult(socialReturn).finally(() => {
        cleanupQueryParams();
      });
      return;
    }

    // A social_flow landing whose params can't be parsed (e.g. provider returned no email)
    // would otherwise leave the pending loader up forever — fall back to the email step.
    if (socialAuthPending.value) {
      $logWarnMessage('Social auth return unparseable', { provider: route.query.social_provider });
      socialAuthPending.value = false;
      cleanupQueryParams();
    }

    $analytics.track(EVENTS.LOGIN_PAGE_VIEWED, { roleType });
    $analytics.track(EVENTS.UNIFIED_AUTH_LOGIN_STARTED, {
      entrySource,
      entryCta,
      roleType,
    });
  });
};
